import { EmptyState } from '@monorepo/shared/componentsV2/EmptyState';
import { getDocumentFileColumns } from '@monorepo/shared/componentsV2/documents/getDocumentFileColumns';
import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { useSkipWidgetLink } from '@monorepo/shared/componentsV2/SkipWidgetLink';
import { Pagination } from '@monorepo/shared/componentsV2/Table/Pagination';
import { Table } from '@monorepo/shared/componentsV2/Table/Table';
import { useFrontendPagination } from '@monorepo/shared/hooks/useFrontendPagination';
import { getDocumentUploadEmptyStateSecondaryText } from '@monorepo/shared/utils/documentUpload';
import { lookFor } from '@monorepo/shared/utils/lookFor';
import { DocumentFileResponse } from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useSiteFolderDocumentsList } from '../../hooks/useSiteFolderDocumentsList';
import { StyledMain } from '../styled';
import { SiteDocumentsTableActions } from '../SiteDocumentsTableActions';
import { OrganizationScopedFolderIntro } from './OrganizationScopedFolderIntro';

const StyledTable = styled(Table)`
  height: 100%;
  padding-bottom: 4rem;
` as typeof Table;

function getEmptyStateSecondaryText(
  isOrganizationScopedFolder: boolean,
  canEditSiteDocuments: boolean,
): string {
  if (isOrganizationScopedFolder) {
    return 'All-sites folder documents are managed in Setup. They will appear here automatically when an organization admin adds them.';
  }
  if (canEditSiteDocuments) {
    return getDocumentUploadEmptyStateSecondaryText({
      uploadButtonLabel: 'Add documents',
    });
  }
  return 'Your organization or site admin can add documents to this folder.';
}

interface SiteFolderDocumentsTableProps {
  organizationId: string;
  projectId: string;
  folderId: string;
  isOrganizationScopedFolder: boolean;
  filter: string;
  canEditSiteDocuments: boolean;
}

function getColumns(
  organizationId: string,
  projectId: string,
  canEditSiteDocuments: boolean,
) {
  return getDocumentFileColumns({
    actions: (document) => (
      <SiteDocumentsTableActions
        organizationId={organizationId}
        projectId={projectId}
        document={document}
        canEditSiteDocuments={canEditSiteDocuments}
      />
    ),
  });
}

export function SiteFolderDocumentsTable({
  organizationId,
  projectId,
  folderId,
  isOrganizationScopedFolder,
  filter,
  canEditSiteDocuments,
}: SiteFolderDocumentsTableProps) {
  const { documents, isLoading } = useSiteFolderDocumentsList({
    organizationId,
    projectId,
    folderId,
  });

  const columns = useMemo(
    () => getColumns(organizationId, projectId, canEditSiteDocuments),
    [canEditSiteDocuments, organizationId, projectId],
  );

  const filterRowsBy = useCallback(
    (row: DocumentFileResponse) => lookFor(filter, row, ['name']),
    [filter],
  );

  const { page, setPage, filteredResults, rows, rowsPerPage } =
    useFrontendPagination({
      items: documents,
      filterRowsBy,
      resetPageWhen: [folderId, filter],
    });

  const { SkipLink, SkipLinkTarget } = useSkipWidgetLink();

  if (isLoading) {
    return <Loading />;
  }

  const showOrganizationScopedIntro =
    isOrganizationScopedFolder && canEditSiteDocuments;

  return (
    <StyledMain>
      {showOrganizationScopedIntro && (
        <OrganizationScopedFolderIntro
          organizationId={organizationId}
          folderId={folderId}
        />
      )}
      {documents?.length ? (
        <>
          <SkipLink linkText="Skip to pagination" />
          <StyledTable
            ariaLabel="Site folder documents"
            columns={columns}
            rows={rows}
            isPrefilteredAndNotEmpty
            makeTableFocusable
          />
          <SkipLinkTarget />
          <Pagination
            totalRowCount={filteredResults.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
          />
        </>
      ) : (
        <EmptyState
          primaryText={
            isOrganizationScopedFolder
              ? 'There are no documents in this all-sites folder yet.'
              : 'There are no documents in this folder for this site yet.'
          }
          secondaryText={getEmptyStateSecondaryText(
            isOrganizationScopedFolder,
            canEditSiteDocuments,
          )}
        />
      )}
    </StyledMain>
  );
}
